class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.isHover = false;
    this.hoverTimeout = null;
    this.clickTimeout = null;
    
    // Enhanced event handling for better UX
    this.setupEventListeners();
    
    // Handle responsive behavior
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  setupEventListeners() {
    const summary = this.mainDetailsToggle.querySelector('summary');
    const submenu = this.mainDetailsToggle.querySelector('.header__submenu, .mega-menu__content');
    
    if (!summary || !submenu) return;

    // Remove existing listeners to prevent duplicates
    this.removeEventListeners();

    // Mouse events for hover behavior (desktop only)
    if (window.innerWidth >= 990) {
      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }
    
    // Click events for mobile/accessibility
    summary.addEventListener('click', this.onSummaryClick.bind(this));
    
    // Keyboard navigation
    summary.addEventListener('keydown', this.onKeyDown.bind(this));
    
    // Focus management
    this.addEventListener('focusin', this.onFocusIn.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    
    // Store references for cleanup
    this.boundMouseEnter = this.onMouseEnter.bind(this);
    this.boundMouseLeave = this.onMouseLeave.bind(this);
    this.boundSummaryClick = this.onSummaryClick.bind(this);
    this.boundKeyDown = this.onKeyDown.bind(this);
    this.boundFocusIn = this.onFocusIn.bind(this);
    this.boundFocusOut = this.onFocusOut.bind(this);
  }

  removeEventListeners() {
    if (this.boundMouseEnter) {
      this.removeEventListener('mouseenter', this.boundMouseEnter);
    }
    if (this.boundMouseLeave) {
      this.removeEventListener('mouseleave', this.boundMouseLeave);
    }
    if (this.boundFocusIn) {
      this.removeEventListener('focusin', this.boundFocusIn);
    }
    if (this.boundFocusOut) {
      this.removeEventListener('focusout', this.boundFocusOut);
    }
    
    const summary = this.mainDetailsToggle?.querySelector('summary');
    if (summary) {
      if (this.boundSummaryClick) {
        summary.removeEventListener('click', this.boundSummaryClick);
      }
      if (this.boundKeyDown) {
        summary.removeEventListener('keydown', this.boundKeyDown);
      }
    }
  }

  onMouseEnter() {
    clearTimeout(this.hoverTimeout);
    this.isHover = true;
    
    // Small delay to prevent accidental opens
    this.hoverTimeout = setTimeout(() => {
      if (this.isHover) {
        this.open();
      }
    }, 100);
  }

  onMouseLeave() {
    clearTimeout(this.hoverTimeout);
    this.isHover = false;
    
    // Delay close to allow moving between menu items
    this.hoverTimeout = setTimeout(() => {
      if (!this.isHover && !this.contains(document.activeElement)) {
        this.close();
      }
    }, 200);
  }

  onSummaryClick(event) {
    // Prevent default details behavior
    event.preventDefault();
    
    // Clear any pending hover actions
    clearTimeout(this.hoverTimeout);
    clearTimeout(this.clickTimeout);
    
    // Toggle menu state
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  onKeyDown(event) {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.onSummaryClick(event);
        break;
      case 'Escape':
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.open();
        this.focusFirstMenuItem();
        break;
    }
  }

  onFocusIn() {
    clearTimeout(this.hoverTimeout);
  }

  onFocusOut() {
    // Enhanced focus out handling
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.isHover) {
        this.close();
      }
    }, 100);
  }

  open() {
    if (this.mainDetailsToggle.hasAttribute('open')) return;
    
    // Close other open menus
    this.closeOtherMenus();
    
    // Open this menu
    this.mainDetailsToggle.setAttribute('open', '');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'true');
    }
    
    // Trigger toggle event for animations
    this.onToggle();
    
    // Set up click outside handler
    this.setupClickOutside();
  }

  close() {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    
    this.mainDetailsToggle.removeAttribute('open');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
    }
    
    // Remove click outside handler
    this.removeClickOutside();
  }

  closeOtherMenus() {
    // Close all other header menus
    document.querySelectorAll('header-menu').forEach(menu => {
      if (menu !== this) {
        menu.close();
      }
    });
  }

  focusFirstMenuItem() {
    const firstLink = this.mainDetailsToggle.querySelector('.header__submenu a, .mega-menu__content a');
    if (firstLink) {
      firstLink.focus();
    }
  }

  setupClickOutside() {
    this.clickOutsideHandler = (event) => {
      if (!this.contains(event.target)) {
        this.close();
      }
    };
    
    // Small delay to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideHandler);
    }, 10);
  }

  removeClickOutside() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
      this.clickOutsideHandler = null;
    }
  }

  onToggle() {
    if (!this.header) return;
    
    // Prevent header from hiding when menu is open
    this.header.preventHide = this.mainDetailsToggle.hasAttribute('open');

    // Set header bottom position for proper positioning
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(this.header.getBoundingClientRect().bottom)}px`);
    
    // Enhanced animation handling
    super.onToggle();
  }

  handleResize() {
    // Close menu on resize to prevent positioning issues
    this.close();
    
    // Reinitialize event listeners for responsive behavior
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.setupEventListeners();
    }, 100);
  }

  // Clean up on disconnect
  disconnectedCallback() {
    clearTimeout(this.hoverTimeout);
    clearTimeout(this.clickTimeout);
    clearTimeout(this.resizeTimeout);
    this.removeClickOutside();
    this.removeEventListeners();
    
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }
}

customElements.define('header-menu', HeaderMenu);
